import type { AppDispatch, RootState } from '../../store';
import { updateElements } from '../layout/layoutSlice';
import { updateInstance } from '../equipment/equipmentSlice';

export type OutsideStockerTarget =
  | { kind: 'layout'; id: string }
  | { kind: 'equipment'; id: string };

/**
 * Set (or clear) Outside Stocker on one stocker. When enabling, clears the flag
 * on every other layout and equipment stocker so at most one is marked.
 */
export const setOutsideStockerExclusive =
  (target: OutsideStockerTarget, enabled: boolean) =>
  (dispatch: AppDispatch, getState: () => RootState) => {
    const state = getState();
    const layoutPatches: Array<{
      id: string;
      updates: { customProperties: { outsideStocker: boolean } };
    }> = [];

    for (const element of Object.values(state.layout.elements)) {
      if (element.type !== 'stocker') continue;
      const isTarget = target.kind === 'layout' && element.id === target.id;
      const next = isTarget ? enabled : false;
      if ((element.customProperties?.outsideStocker === true) === next) continue;
      // When disabling, only touch the target; leave other stockers alone.
      if (!enabled && !isTarget) continue;
      layoutPatches.push({
        id: element.id,
        updates: { customProperties: { outsideStocker: next } },
      });
    }

    if (layoutPatches.length > 0) {
      dispatch(updateElements(layoutPatches));
    }

    for (const instance of Object.values(state.equipment.instances)) {
      if (instance.objectType !== 'stocker') continue;
      const isTarget = target.kind === 'equipment' && instance.id === target.id;
      const next = isTarget ? enabled : false;
      if (Boolean(instance.outsideStocker) === next) continue;
      if (!enabled && !isTarget) continue;
      dispatch(
        updateInstance({
          id: instance.id,
          changes: { outsideStocker: next },
          skipHistory: !isTarget,
        })
      );
    }
  };
